import { Reminder, ReminderBasedOn } from '@griply/types';

import { isSameReminder } from './reminder-list';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

// All-day items have no time-of-day, so their reminders fire at 09:00.
const ALL_DAY_REMINDER_TIME_MS = 9 * HOUR_MS;

// Defaults a user gets when they never set the reminder (setting is undefined) —
// mirrors the backend: a timed task reminds at its start, an all-day one at 09:00.
// An explicit null is "off"; these apply only to undefined.
export const DEFAULT_PLANNED_REMINDER_OFFSET_MS = 0;
export const DEFAULT_ALL_DAY_REMINDER_OFFSET_MS = ALL_DAY_REMINDER_TIME_MS;

export type ReminderPreset = {
  offset: number;
  basedOn: ReminderBasedOn;
  labelKey: string;
};

export const TIMED_REMINDER_PRESETS: ReminderPreset[] = [
  {
    offset: 0,
    basedOn: ReminderBasedOn.Start,
    labelKey: 'reminders.presets.at-time',
  },
  {
    offset: -5 * MINUTE_MS,
    basedOn: ReminderBasedOn.Start,
    labelKey: 'reminders.presets.5-min-before',
  },
  {
    offset: -10 * MINUTE_MS,
    basedOn: ReminderBasedOn.Start,
    labelKey: 'reminders.presets.10-min-before',
  },
  {
    offset: -15 * MINUTE_MS,
    basedOn: ReminderBasedOn.Start,
    labelKey: 'reminders.presets.15-min-before',
  },
  {
    offset: -30 * MINUTE_MS,
    basedOn: ReminderBasedOn.Start,
    labelKey: 'reminders.presets.30-min-before',
  },
  {
    offset: -1 * HOUR_MS,
    basedOn: ReminderBasedOn.Start,
    labelKey: 'reminders.presets.1-hour-before',
  },
  {
    offset: -2 * HOUR_MS,
    basedOn: ReminderBasedOn.Start,
    labelKey: 'reminders.presets.2-hours-before',
  },
  {
    offset: -1 * DAY_MS,
    basedOn: ReminderBasedOn.Start,
    labelKey: 'reminders.presets.1-day-before',
  },
  {
    offset: -2 * DAY_MS,
    basedOn: ReminderBasedOn.Start,
    labelKey: 'reminders.presets.2-days-before',
  },
];

export const ALL_DAY_REMINDER_PRESETS: ReminderPreset[] = [
  {
    offset: ALL_DAY_REMINDER_TIME_MS,
    basedOn: ReminderBasedOn.DayStart,
    labelKey: 'reminders.presets.on-the-day',
  },
  {
    offset: ALL_DAY_REMINDER_TIME_MS - 1 * DAY_MS,
    basedOn: ReminderBasedOn.DayStart,
    labelKey: 'reminders.presets.1-day-before-9am',
  },
  {
    offset: ALL_DAY_REMINDER_TIME_MS - 2 * DAY_MS,
    basedOn: ReminderBasedOn.DayStart,
    labelKey: 'reminders.presets.2-days-before-9am',
  },
  {
    offset: ALL_DAY_REMINDER_TIME_MS - 1 * WEEK_MS,
    basedOn: ReminderBasedOn.DayStart,
    labelKey: 'reminders.presets.1-week-before-9am',
  },
];

const ALL_REMINDER_PRESETS = [
  ...TIMED_REMINDER_PRESETS,
  ...ALL_DAY_REMINDER_PRESETS,
];

export const getReminderOptions = (
  basedOn: ReminderBasedOn,
): ReminderPreset[] =>
  basedOn === ReminderBasedOn.Start
    ? TIMED_REMINDER_PRESETS
    : ALL_DAY_REMINDER_PRESETS;

export const findReminderPreset = (reminder: Reminder): ReminderPreset | null =>
  ALL_REMINDER_PRESETS.find((preset) => isSameReminder(preset, reminder)) ??
  null;

// Reminders the item holds that no preset covers — they need their own row.
export const getNonPresetReminders = (
  reminders: Reminder[],
  presets: ReminderPreset[],
): Reminder[] =>
  reminders.filter(
    (reminder) => !presets.some((preset) => isSameReminder(reminder, preset)),
  );
